import { DSButtonV2 } from '@elliemae/ds-button';
import { DSCircularIndeterminateIndicator } from '@elliemae/ds-circular-progress-indicator';
import { DSFormItemLayout, DSLargeInputText } from '@elliemae/ds-form';
import { DSModal, MODAL_TYPE_V2 } from '@elliemae/ds-modal';
import { useLookupOptions, useUserData } from '@frontend/pricing-search';
import { formatDate } from '@frontend/utils';
import { memo, useEffect, useMemo, useState } from 'react';
import {
  useLoanDataSelector,
  useLoanInformationField,
} from '../../../../../data/useSelectors';
import type {
  EligibilityException,
  ExceptionBorrowerFinancials,
  ExceptionFieldErrors,
  ExceptionFieldId,
  ExceptionProgramData,
  LoanExceptionUpsertRequest,
} from '../types';
import {
  CANCEL_LABEL,
  COMMENTS_MAX_LENGTH,
  DELETE_AND_QUALIFY_LABEL,
  DIALOG_TITLE,
  MANAGE_DIALOG_TITLE,
  SAVE_AND_QUALIFY_LABEL,
} from '../utils/constants';
import {
  buildDialogRows,
  buildExceptionFieldValues,
  buildLoanExceptionUpsertRequest,
  mergeExceptionFieldValues,
  parseIntegerInput,
  resolveEnteredBy,
  resolveEnteredDate,
  validateExceptionRanges,
} from '../utils/helpers';
import { FieldTable, type FieldInputs } from './FieldTable';
import {
  StyledBusyOverlay,
  StyledDialogContent,
  StyledDialogFooter,
  StyledMetaRow,
  StyledProgramName,
} from './styled';

export interface SaveAndQualifyPayload {
  programId: number;
  body: LoanExceptionUpsertRequest;
}

export interface ExceptionDialogProps {
  isOpen: boolean;
  mode: 'apply' | 'manage';
  program: ExceptionProgramData | null;
  existingException?: EligibilityException;
  // True while the saved exception is being fetched (manage mode), so the
  // dialog shows a loader instead of the half-populated form.
  isLoading?: boolean;
  onClose: () => void;
  // Deletes the whole exception then re-qualifies. Resolves true on success
  // (dialog closes); false keeps it open with the current state intact.
  onDelete?: () => Promise<boolean>;
  // Emits the upsert body + programId so the host can save and re-qualify.
  // Resolves true when the save succeeded (dialog closes); false keeps it open.
  onSaveAndQualify?: (payload: SaveAndQualifyPayload) => Promise<boolean>;
}

const buildInitialInputs = (
  rows: ReturnType<typeof buildDialogRows>,
): Partial<Record<ExceptionFieldId, FieldInputs>> =>
  rows.reduce<Partial<Record<ExceptionFieldId, FieldInputs>>>((acc, row) => {
    acc[row.exceptionFieldId] = {
      adjustment: row.adjustment !== null ? String(row.adjustment) : '',
      priceAdjustment:
        row.priceAdjustment !== null ? String(row.priceAdjustment) : '',
    };
    return acc;
  }, {});

const ExceptionDialogComponent = ({
  isOpen,
  mode,
  program,
  existingException,
  isLoading = false,
  onClose,
  onDelete,
  onSaveAndQualify,
}: ExceptionDialogProps) => {
  const isManage = mode === 'manage';
  const modalTestId = isManage
    ? 'manage-eligibility-exception-modal'
    : 'apply-eligibility-exception-modal';
  const { firstName = '', lastName = '' } = useUserData();
  const currentUser = [firstName, lastName].filter(Boolean).join(' ');
  const programName = program?.program ?? '';
  // Guideline <- program.eligibilityDetails (apply) or the saved exception
  // (manage); current <- the rates payload (loanInformation).
  const loanInformation = useLoanInformationField();
  const borrowerFinancials = useLoanDataSelector(
    'borrowers[0].borrowerFinanancial',
  ) as ExceptionBorrowerFinancials | null;
  const fieldValues = useMemo(
    () =>
      mergeExceptionFieldValues(
        buildExceptionFieldValues(program, loanInformation, borrowerFinancials),
        existingException,
      ),
    [program, loanInformation, borrowerFinancials, existingException],
  );
  const rows = useMemo(
    () => buildDialogRows(fieldValues, existingException),
    [fieldValues, existingException],
  );

  const [inputs, setInputs] = useState<
    Partial<Record<ExceptionFieldId, FieldInputs>>
  >(() => buildInitialInputs(rows));
  const [comments, setComments] = useState(existingException?.comments ?? '');
  const [errors, setErrors] = useState<
    Partial<Record<ExceptionFieldId, ExceptionFieldErrors>>
  >({});

  // Reset local state whenever the dialog (re)opens for a program.
  useEffect(() => {
    if (isOpen) {
      setInputs(buildInitialInputs(rows));
      setComments(existingException?.comments ?? '');
      setErrors({});
    }
  }, [isOpen, rows, existingException]);

  const clearRowErrors = (exceptionFieldId: ExceptionFieldId) =>
    setErrors((prev) =>
      prev[exceptionFieldId]
        ? { ...prev, [exceptionFieldId]: undefined }
        : prev,
    );

  const handleAdjustmentChange = (
    exceptionFieldId: ExceptionFieldId,
    raw: string,
  ) => {
    clearRowErrors(exceptionFieldId);
    setInputs((prev) => ({
      ...prev,
      [exceptionFieldId]: {
        ...prev[exceptionFieldId],
        adjustment: raw,
        // Clear an orphaned price adjustment when the eligibility delta is removed.
        priceAdjustment:
          parseIntegerInput(raw) === null || parseIntegerInput(raw) === 0
            ? ''
            : prev[exceptionFieldId]?.priceAdjustment ?? '',
      },
    }));
  };

  const handlePriceChange = (
    exceptionFieldId: ExceptionFieldId,
    raw: string,
  ) => {
    clearRowErrors(exceptionFieldId);
    setInputs((prev) => ({
      ...prev,
      [exceptionFieldId]: {
        ...prev[exceptionFieldId],
        priceAdjustment: raw,
      },
    }));
  };

  // "Save and Qualify" requires at least one non-zero eligibility adjustment.
  const canSave = useMemo(
    () =>
      Object.values(inputs).some((value) => {
        const adjustment = parseIntegerInput(value.adjustment);
        return adjustment !== null && adjustment !== 0;
      }),
    [inputs],
  );

  // Manage mode resolves the saved author (from userId) and date; apply mode
  // shows the current user and today (the creator of a new exception).
  const officeUsers = useLookupOptions('transformedOfficeUsers') as Array<{
    value?: number;
    label?: string;
  }>;
  const enteredBy =
    resolveEnteredBy(mode, existingException, officeUsers) ?? currentUser;
  const enteredDate =
    resolveEnteredDate(mode, existingException) ??
    formatDate(new Date(), 'Calendar');

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isBusy = isSaving || isDeleting || isLoading;
  const hasSavedException = Boolean(existingException);

  const handleSaveAndQualify = async () => {
    // Flag out of range rows and skip both the save and the re-qualify.
    const rangeErrors = validateExceptionRanges(rows, inputs);
    setErrors(rangeErrors);
    if (Object.keys(rangeErrors).length > 0) {
      return;
    }

    // Hand the assembled body to the host to save and re-qualify. Only close on
    // success; on failure the dialog stays open with the user's inputs intact.
    const body = buildLoanExceptionUpsertRequest(rows, inputs, comments);
    const programId = program?.programID ?? existingException?.programId ?? 0;
    if (!body || !onSaveAndQualify) {
      onClose();
      return;
    }
    setIsSaving(true);
    const succeeded = await onSaveAndQualify({ programId, body });
    setIsSaving(false);
    if (succeeded) {
      onClose();
    }
  };

  const handleDelete = async () => {
    // Delete the whole exception then re-qualify.
    if (!onDelete) {
      return;
    }
    setIsDeleting(true);
    const succeeded = await onDelete();
    setIsDeleting(false);
    if (succeeded) {
      onClose();
    }
  };

  return (
    <>
      <style>{`[data-testid="${modalTestId}"] [data-testid="modal-footer-wrapper"]{display:none!important}`}</style>
      <DSModal
        isOpen={isOpen}
        version={2}
        centered
        size="x-large"
        modalType={MODAL_TYPE_V2.FORM}
        modalTitle={isManage ? MANAGE_DIALOG_TITLE : DIALOG_TITLE}
        showClose
        onClose={onClose}
        dataTestId={modalTestId}
      >
        <StyledDialogContent>
          {isLoading && (
            <StyledBusyOverlay data-testid="exception-busy-overlay">
              <DSCircularIndeterminateIndicator size="l" color="dark" />
            </StyledBusyOverlay>
          )}
          <StyledProgramName>
            <span className="label">Program Name</span>
            <span className="value">{programName}</span>
          </StyledProgramName>

          <FieldTable
            rows={rows}
            inputs={inputs}
            errors={errors}
            onAdjustmentChange={handleAdjustmentChange}
            onPriceChange={handlePriceChange}
          />

          <StyledMetaRow>
            <div>
              <span className="label">Entered By</span>
              <span className="value">{enteredBy}</span>
            </div>
            <div>
              <span className="label">Entered Date</span>
              <span className="value">{enteredDate}</span>
            </div>
          </StyledMetaRow>

          <DSFormItemLayout
            labelText="Comments"
            value={comments}
            maxLength={COMMENTS_MAX_LENGTH}
            characterLimitCounter={COMMENTS_MAX_LENGTH}
            rows={3}
            inputComponent={DSLargeInputText}
            onChange={(e) => setComments(e.target.value)}
            data-testid="exception-comments"
          />

          <StyledDialogFooter>
            {hasSavedException && (
              <DSButtonV2
                buttonType="outline"
                disabled={isBusy}
                onClick={handleDelete}
                data-testid="delete-exception-button"
              >
                {DELETE_AND_QUALIFY_LABEL}
              </DSButtonV2>
            )}
            <DSButtonV2 buttonType="outline" onClick={onClose}>
              {CANCEL_LABEL}
            </DSButtonV2>
            <DSButtonV2
              buttonType="filled"
              disabled={!canSave || isBusy}
              onClick={handleSaveAndQualify}
              data-testid="save-and-qualify-button"
            >
              {SAVE_AND_QUALIFY_LABEL}
            </DSButtonV2>
          </StyledDialogFooter>
        </StyledDialogContent>
      </DSModal>
    </>
  );
};

export const ExceptionDialog = memo(ExceptionDialogComponent);
